import { useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { useRequestContractCancellation } from '@/features/contracts/hooks/useContracts';
import { FormSelect } from '@/Components/shared/FormSelect';
import { FormTextarea } from '@/Components/shared/FormTextarea';
import { USER_CANCELLATION_REASONS } from '@/features/contracts/constants/cancellationReasons';

interface RequestCancellationModalProps {
  contractId: number;
  contractLabel: string;
  isOpen: boolean;
  onClose: () => void;
}

export function RequestCancellationModal({
  contractId,
  contractLabel,
  isOpen,
  onClose,
}: RequestCancellationModalProps) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const { mutate, isLoading } = useRequestContractCancellation();

  if (!isOpen) return null;

  const isDetailsRequired = reason === 'other';
  const isValid = reason !== '' && (!isDetailsRequired || details.trim() !== '');

  const handleSubmit = () => {
    if (!isValid) return;
    mutate(
      { id: contractId, reason, details: details.trim() || undefined },
      {
        onSuccess: () => {
          onClose();
          setReason('');
          setDetails('');
        },
      },
    );
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      {/*
        Plafond + `overflow-hidden` + colonne flex : sans eux, un panneau plus haut
        que la fenêtre est centré par le flex parent, donc son haut sort par le
        dessus — hors d'atteinte, et rien ne défile puisque l'overlay est `fixed`.
      */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-lg tw-w-full tw-max-h-[90vh] tw-overflow-hidden tw-flex tw-flex-col">
        <div className="tw-flex tw-items-center tw-justify-between tw-p-5 tw-border-b tw-border-gray-200 tw-flex-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <AlertTriangle className="tw-w-5 tw-h-5 tw-text-orange-500" />
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">
              Demander l'annulation
            </h3>
          </div>
          <button onClick={onClose} disabled={isLoading} className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors">
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>
        <div className="tw-p-5 tw-space-y-4 tw-flex-1 tw-overflow-y-auto">
          <p className="tw-text-sm tw-text-gray-600 tw-m-0">
            Vous allez demander l'annulation du contrat <strong>{contractLabel}</strong>.
            Un email sera envoyé à l'équipe Affinicar pour traiter votre demande.
          </p>
          <FormSelect
            value={reason}
            onChange={setReason}
            options={[...USER_CANCELLATION_REASONS]}
            label="Raison de l'annulation"
            placeholder="Sélectionnez une raison..."
            required
            disabled={isLoading}
          />
          <FormTextarea
            value={details}
            onChange={setDetails}
            label="Détails"
            placeholder="Précisez la raison de l'annulation..."
            required={isDetailsRequired}
            rows={3}
            disabled={isLoading}
          />
        </div>
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-5 tw-border-t tw-border-gray-200 tw-flex-shrink-0">
          <button onClick={onClose} disabled={isLoading} className="tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50">
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={!isValid || isLoading} className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-orange-500 hover:tw-bg-orange-600 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
            {isLoading && <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />}
            Confirmer la demande
          </button>
        </div>
      </div>
    </div>
  );
}
